import {
    useCallback, useEffect, useMemo, useRef,
} from 'react';
import { PageSpecificKeys } from 'helpers/localStorage/constants';
import useLocalStorage from 'helpers/localStorage/useLocalStorage';
import { getLocalStorage, storeLocalStorage } from 'helpers/localStorage/helpers';
import useAuthContext from 'contexts/AuthContext';
import {
    getSensitivityConfigById,
    isValidSensitivityId,
    getSensitivityOptions,
    getSensitivityTypeFromId,
} from '../constants';

interface TableConfig {
    id: string;
    type: string;
    tableLabel: string;
}

interface SensitivityIncrement {
    vertical: number;
    horizontal: number;
}

// Storage structure: { [proFormaId]: { [sensitivityTypeId]: { verticalAxis, horizontalAxis } } }
type SimplifiedSensitivityStorage = Record<string, SensitivityIncrement>;

interface UseSensitivityStorageReturn {
    saveSensitivity: (sensitivityId: string, vertical: number, horizontal: number) => void;
    removeSensitivity: (sensitivityId: string) => void;
    loadSensitivities: () => TableConfig[];
    clearSensitivities: () => void;
    getIncrements: (sensitivityId: string, proFormaModelId?: number) => SensitivityIncrement;
}

type SensitivityStorageType = { [proFormaId: string]: SimplifiedSensitivityStorage } | SimplifiedSensitivityStorage | string[] | null;

// Global storage lock to prevent race conditions
let storageOperationInProgress = false;
const pendingOperations: (() => void)[] = [];

const executeStorageOperation = (operation: () => void) => {
    if (storageOperationInProgress) {
        pendingOperations.push(operation);
        return;
    }

    storageOperationInProgress = true;
    operation();
    storageOperationInProgress = false;

    // Execute next pending operation
    if (pendingOperations.length > 0) {
        const nextOperation = pendingOperations.shift()!;
        setTimeout(() => executeStorageOperation(nextOperation), 0);
    }
};

const getStorageData = (userId?: number): { [proFormaId: string]: SimplifiedSensitivityStorage } => {
    const rawStorage = getLocalStorage<SensitivityStorageType>(PageSpecificKeys.SensitivityTables, false, userId);
    if (!rawStorage || Array.isArray(rawStorage)) return {};
    if (typeof rawStorage !== 'object') return {};
    return rawStorage as { [proFormaId: string]: SimplifiedSensitivityStorage };
};

export const useSensitivityStorage = (proFormaId: string, proFormaModelId?: number): UseSensitivityStorageReturn => {
    const { user } = useAuthContext();
    const [rawStorage, setRawStorage] = useLocalStorage<SensitivityStorageType>(PageSpecificKeys.SensitivityTables, null);
    const currentData = useMemo(() => {
        if (!rawStorage || Array.isArray(rawStorage)) return {};
        return rawStorage as { [proFormaId: string]: SimplifiedSensitivityStorage };
    }, [rawStorage]);

    // Keep a ref to the current storage for consistent reads across rapid updates
    const currentStorageRef = useRef<{ [proFormaId: string]: SimplifiedSensitivityStorage }>({});

    useEffect(() => {
        currentStorageRef.current = currentData;
    }, [currentData]);

    const getDefaultIncrements = useCallback((sensitivityId: string, proFormaModelId?: number): SensitivityIncrement => {
        const options = getSensitivityOptions(proFormaModelId);
        const sensitivityType = getSensitivityTypeFromId(sensitivityId);
        const option = options.find(opt => opt.value === sensitivityType);

        return option?.increments || { vertical: 1, horizontal: 0.01 };
    }, []);

    // Simple migration: clear old format, let new format populate naturally
    const didAttemptMigration = useRef(false);
    useEffect(() => {
        if (didAttemptMigration.current) return;
        const isArrayFormat = Array.isArray(rawStorage);
        const hasOldFormat = rawStorage && typeof rawStorage === 'object' && Object.values(rawStorage).some(data => Array.isArray(data));
        if (isArrayFormat || hasOldFormat) {
            setRawStorage({});
            didAttemptMigration.current = true;
        }
    }, [rawStorage, setRawStorage]);

    const saveSensitivity = useCallback((sensitivityId: string, vertical: number, horizontal: number) => {
        if (!proFormaId || !sensitivityId) return;

        executeStorageOperation(() => {
            const currentData = getStorageData(user?.id);
            const proFormaData = currentData[proFormaId] || {};

            const newState = {
                ...currentData,
                [proFormaId]: {
                    ...proFormaData,
                    [sensitivityId]: { vertical, horizontal },
                },
            };

            storeLocalStorage(PageSpecificKeys.SensitivityTables, newState, false, user?.id);
            currentStorageRef.current = newState;
            setRawStorage(newState);
        });
    }, [proFormaId, user?.id, setRawStorage]);

    const removeSensitivity = useCallback((sensitivityId: string) => {
        if (!proFormaId || !sensitivityId) return;

        executeStorageOperation(() => {
            const currentData = getStorageData(user?.id);
            const proFormaData = { ...(currentData[proFormaId] || {}) };
            delete proFormaData[sensitivityId];

            const newState = {
                ...currentData,
                [proFormaId]: proFormaData,
            };

            storeLocalStorage(PageSpecificKeys.SensitivityTables, newState, false, user?.id);
            currentStorageRef.current = newState;
            setRawStorage(newState);
        });
    }, [proFormaId, user?.id, setRawStorage]);

    const loadSensitivities = useCallback((): TableConfig[] => {
        if (!proFormaId) return [];

        const proFormaData = currentStorageRef.current[proFormaId];
        if (!proFormaData) return [];

        // Get all sensitivity IDs that have saved increments
        return Object.keys(proFormaData)
            .filter((id: string) => isValidSensitivityId(id))
            .map((id: string) => getSensitivityConfigById(id, proFormaModelId))
            .filter((config): config is TableConfig => config !== null);
    }, [proFormaId, proFormaModelId]);

    const clearSensitivities = useCallback(() => {
        if (!proFormaId) return;

        executeStorageOperation(() => {
            const currentData = getStorageData(user?.id);

            const newState = {
                ...currentData,
                [proFormaId]: {},
            };

            storeLocalStorage(PageSpecificKeys.SensitivityTables, newState, false, user?.id);
            currentStorageRef.current = newState;
            setRawStorage(newState);
        });
    }, [proFormaId, user?.id, setRawStorage]);

    const getIncrements = useCallback((sensitivityId: string, proFormaModelId?: number): SensitivityIncrement => {
        if (!proFormaId || !sensitivityId) {
            return getDefaultIncrements(sensitivityId, proFormaModelId);
        }

        const proFormaData = currentStorageRef.current[proFormaId];
        if (!proFormaData) return getDefaultIncrements(sensitivityId, proFormaModelId);

        const savedIncrements = proFormaData[sensitivityId];
        return savedIncrements || getDefaultIncrements(sensitivityId, proFormaModelId);
    }, [proFormaId, getDefaultIncrements]);

    return {
        saveSensitivity,
        removeSensitivity,
        loadSensitivities,
        clearSensitivities,
        getIncrements,
    };
};
